import { parseISO } from 'date-fns';
import { z } from 'zod';

import {
  isDate,
  isPostalCode,
  isUUID,
  isValidPhoneNumber,
} from '@/utils/helpers';

const nullableId = z
  .string()
  .refine((val: string) => isUUID(val), 'Invalid value')
  .nullable();

const emailSchema = z.union([z.literal(''), z.string().email().nullable()]);

export const massUpdateSchema = z.object({
  owner: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  ownerId: nullableId.optional(),
  needByDate: z
    .string()
    .refine((value) => {
      return value ? isDate(parseISO(value)) : true;
    })
    .nullable()
    .optional(),
  originalNeedByDate: z
    .string()
    .refine((value) => {
      return value ? isDate(new Date(value)) : true;
    })
    .nullable()
    .optional(),
  shipmentStatus: z.number().nullable().optional(),
  shipFromSite: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  shipFromSiteId: nullableId.optional(),
  date: z
    .string()
    .refine((value) => {
      return value ? isDate(new Date(value)) : true;
    })
    .nullable()
    .optional(),
  requestedBy: z.string().nullable().optional(),
  phone: z
    .string()
    .refine(
      (phone) => (phone ? isValidPhoneNumber(phone) : true),
      'Must be a phone number.',
    )
    .nullable()
    .optional(),
  email: emailSchema.optional(),
  shipTo: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  shipToId: nullableId.optional(),
  shipName: z.string().nullable().optional(),
  shipAddress: z.string().nullable().optional(),
  shipAddress2: z.string().nullable().optional(),
  shipAddress3: z.string().nullable().optional(),
  shipCity: z.string().nullable().optional(),
  shipState: z.string().nullable().optional(),
  shipZip: z
    .string()
    .nullable()
    .refine(
      (value) => (value ? isPostalCode(value) : true),
      'Must be a valid postal code',
    )
    .optional(),
  amount: z.number().min(0).nullable().optional(),
  tank: z
    .string()
    .max(20, 'Must be at most 20 characters long.')
    .nullable()
    .optional(),
  shippingMethod: z.number().nullable().optional(),
  trackingNumber: z.string().nullable().optional(),
  callTag: z.string().nullable().optional(),
  shipmentCategory: z.number().nullable().optional(),
  packedBy: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  packedById: nullableId.optional(),
  billTo: z.string().nullable().optional(),
  billStatus: z.number().nullable().optional(),
  comment: z.string().nullable().optional(),
});

export type MassUpdateSchema = z.infer<typeof massUpdateSchema>;
